import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  Paper,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Tooltip,
  SelectChangeEvent
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  addCategory,
  addSubcategory,
  deleteCategory,
  deleteSubcategory,
  renameCategory,
  renameSubcategory,
  fetchCategories
} from '../store/categorySlice';
import LoadingSpinner from './LoadingSpinner';

const CategoryManagement: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    categories,
    subcategories,
    predefined_categories,
    predefined_subcategories,
    loading
  } = useAppSelector(state => state.categories);

  const [newCategory, setNewCategory] = useState('');
  const [newSubcategory, setNewSubcategory] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [editingCategory, setEditingCategory] = useState<{name: string, newName: string} | null>(null);
  const [editingSubcategory, setEditingSubcategory] = useState<{category: string, name: string, newName: string} | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{type: 'category' | 'subcategory', category: string, subcategory?: string} | null>(null);

  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  const handleTextChange = (setter: (value: string) => void) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (event && event.target) {
      setter((event.target as HTMLInputElement | HTMLTextAreaElement).value);
    }
  };

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    const target = event.target as { value: string };
    setSelectedCategory(target.value);
  };

  const handleCategorySubmit = async () => {
    if (newCategory && !categories.includes(newCategory)) {
      try {
        await dispatch(addCategory(newCategory)).unwrap();
        setNewCategory('');
      } catch (error) {
        // Error adding category
      }
    }
  };

  const handleSubcategorySubmit = async () => {
    if (selectedCategory && newSubcategory && !subcategories[selectedCategory]?.includes(newSubcategory)) {
      try {
        await dispatch(addSubcategory({ category: selectedCategory, subcategory: newSubcategory })).unwrap();
        setNewSubcategory('');
      } catch (error) {
        // Error adding subcategory
      }
    }
  };

  const handleEditCategory = (category: string) => {
    setEditingCategory({ name: category, newName: category });
  };

  const handleSaveCategory = async () => {
    if (editingCategory && editingCategory.newName && editingCategory.name !== editingCategory.newName) {
      try {
        await dispatch(renameCategory({ oldName: editingCategory.name, newName: editingCategory.newName })).unwrap();
      } catch (error) {
        // Error renaming category
      }
    }
    setEditingCategory(null);
  };

  const handleEditSubcategory = (category: string, subcategory: string) => {
    setEditingSubcategory({ category, name: subcategory, newName: subcategory });
  };

  const handleSaveSubcategory = async () => {
    if (editingSubcategory && editingSubcategory.name !== editingSubcategory.newName) {
      try {
        await dispatch(renameSubcategory({
          category: editingSubcategory.category,
          oldName: editingSubcategory.name,
          newName: editingSubcategory.newName
        })).unwrap();
      } catch (error) {
        // Error renaming subcategory
      }
    }
    setEditingSubcategory(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;

    try {
      if (deleteConfirm.type === 'category') {
        await dispatch(deleteCategory(deleteConfirm.category)).unwrap();
      } else if (deleteConfirm.subcategory) {
        await dispatch(deleteSubcategory({
          category: deleteConfirm.category,
          subcategory: deleteConfirm.subcategory
        })).unwrap();
      }
      setDeleteConfirm(null);
    } catch (error) {
      // Error during deletion
    }
  };

  const handleKeyDown = (callback: () => void) => (
    event: React.KeyboardEvent<HTMLDivElement>
  ) => {
    if (event.key === 'Enter') {
      callback();
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ px: { xs: 0, sm: 3 } }}>
        <Box sx={{ my: { xs: 0, sm: 4 }, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <LoadingSpinner />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ px: { xs: 2, sm: 3 } }}>
      <Box sx={{ my: { xs: 0, sm: 4 } }}>
        <Typography variant="h4" component="h1" sx={{ textAlign: 'center', pt: { xs: 1, sm: 0 }, mb: { xs: 1, sm: 2 }, fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
          Category Management
        </Typography>
        <Paper sx={{ p: { xs: 1, sm: 3 }, mb: { xs: 1.5, sm: 3 } }}>
          <Typography variant="h6" gutterBottom>
            Add New Category
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 2 }, mb: { xs: 2, sm: 3 } }}>
            <TextField
              label="Category Name"
              value={newCategory}
              onChange={handleTextChange(setNewCategory)}
              fullWidth
            />
            <Button
              variant="contained"
              onClick={handleCategorySubmit}
              disabled={!newCategory}
              sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
            >
              Add Category
            </Button>
          </Box>

          <Typography variant="h6" gutterBottom>
            Add New Subcategory
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 2 }, alignItems: { xs: 'stretch', sm: 'center' } }}>
            <FormControl sx={{ flex: { xs: 1, sm: 2 } }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={selectedCategory}
                onChange={handleSelectChange}
                label="Category"
              >
                {categories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Subcategory Name"
              value={newSubcategory}
              onChange={handleTextChange(setNewSubcategory)}
              sx={{ flex: { xs: 1, sm: 2 } }}
            />
            <Button
              variant="contained"
              onClick={handleSubcategorySubmit}
              disabled={!selectedCategory || !newSubcategory}
              sx={{
                flex: { xs: 1, sm: 1 },
                height: { xs: 'auto', sm: '56px' },
                whiteSpace: 'nowrap',
                minWidth: { xs: '100%', sm: '160px' },
                py: { xs: 2, sm: 1 }
              }}
            >
              Add Subcategory
            </Button>
          </Box>
        </Paper>

        <Paper sx={{ p: { xs: 1, sm: 3 } }}>
          <Typography variant="h6" gutterBottom>
            Categories and Subcategories
          </Typography>
          <List>
            {categories.map((category) => (
              <Box key={category} sx={{ mb: { xs: 1, sm: 2 } }}>
                <ListItem
                  sx={{
                    bgcolor: 'background.default',
                    borderRadius: 1,
                    '&:hover': { bgcolor: 'action.hover' },
                    flexWrap: { xs: 'wrap', sm: 'nowrap' },
                    gap: { xs: 1, sm: 0 }
                  }}
                >
                  <IconButton
                    onClick={() => setExpandedCategory(expandedCategory === category ? null : category)}
                    size="small"
                  >
                    {expandedCategory === category ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                  <ListItemText
                    sx={{ flex: { xs: '1 1 auto', sm: '1 1 0' } }}
                    primary={
                      editingCategory?.name === category ? (
                        <TextField
                          value={editingCategory.newName}
                          onChange={handleTextChange((value) =>
                            setEditingCategory(prev => prev ? { ...prev, newName: value } : null)
                          )}
                          size="small"
                          autoFocus
                          onKeyDown={handleKeyDown(handleSaveCategory)}
                          onBlur={handleSaveCategory}
                        />
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {category}
                          {predefined_categories.includes(category) && (
                            <Tooltip title="This is a default category and cannot be edited or deleted">
                              <LockIcon fontSize="small" sx={{ ml: 1, color: 'text.secondary', fontSize: '0.8rem' }} />
                            </Tooltip>
                          )}
                        </Box>
                      )
                    }
                  />
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Edit category">
                      <span>
                        <IconButton
                          onClick={() => handleEditCategory(category)}
                          size="medium"
                          disabled={predefined_categories.includes(category)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Delete category">
                      <span>
                        <IconButton
                          onClick={() => setDeleteConfirm({ type: 'category', category })}
                          size="medium"
                          color="error"
                          disabled={predefined_categories.includes(category)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </ListItem>
                <Collapse in={expandedCategory === category}>
                  <List sx={{ pl: { xs: 2, sm: 4 } }}>
                    {subcategories[category]?.map((subcategory) => (
                      <ListItem
                        key={subcategory}
                        sx={{
                          pl: { xs: 1, sm: 2 },
                          '&:hover': { bgcolor: 'action.hover' },
                          borderRadius: 1,
                          flexWrap: { xs: 'wrap', sm: 'nowrap' },
                          gap: { xs: 1, sm: 0 }
                        }}
                      >
                        <ListItemText
                          sx={{ flex: { xs: '1 1 auto', sm: '1 1 0' } }}
                          primary={
                            editingSubcategory?.category === category && editingSubcategory?.name === subcategory ? (
                              <TextField
                                value={editingSubcategory.newName}
                                onChange={handleTextChange((value) =>
                                  setEditingSubcategory(prev => prev ? { ...prev, newName: value } : null)
                                )}
                                size="small"
                                autoFocus
                                onKeyDown={handleKeyDown(handleSaveSubcategory)}
                                onBlur={handleSaveSubcategory}
                              />
                            ) : (
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {subcategory}
                                {predefined_subcategories[category]?.includes(subcategory) && (
                                  <Tooltip title="This is a default subcategory and cannot be edited or deleted">
                                    <LockIcon fontSize="small" sx={{ ml: 1, color: 'text.secondary', fontSize: '0.8rem' }} />
                                  </Tooltip>
                                )}
                              </Box>
                            )
                          }
                        />
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="Edit subcategory">
                            <span>
                              <IconButton
                                onClick={() => handleEditSubcategory(category, subcategory)}
                                size="medium"
                                disabled={predefined_subcategories[category]?.includes(subcategory)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Delete subcategory">
                            <span>
                              <IconButton
                                onClick={() => setDeleteConfirm({ type: 'subcategory', category, subcategory })}
                                size="medium"
                                color="error"
                                disabled={predefined_subcategories[category]?.includes(subcategory)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </Box>
            ))}
          </List>
        </Paper>

        <Dialog
          open={deleteConfirm !== null}
          onClose={() => setDeleteConfirm(null)}
        >
          <DialogTitle>
            {deleteConfirm?.type === 'category' ? 'Delete Category' : 'Delete Subcategory'}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {deleteConfirm?.type === 'category'
                ? `Are you sure you want to delete the category "${deleteConfirm.category}" and all its subcategories? This action cannot be undone.`
                : `Are you sure you want to delete the subcategory "${deleteConfirm?.subcategory}"? This action cannot be undone.`}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button onClick={handleDeleteConfirm} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default CategoryManagement;
